import pushRoute from '@app/utils/routing';
import {
  Box,
  Icons,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@ublend-npm/aulaui-core';
import { useDispatch, useSelector } from 'react-redux';
import { LtiProvider } from '@ublend-npm/aula-schema';
import React, { MouseEvent, useEffect, useRef, useState } from 'react';

import DeleteAssignmentModal from './DeleteAssignmentModal';
import exportSubmissionGrades from '@app/actions/assignments/exportGrades.action';
import { baseDomain } from '@core/selectors/institution';
import {
  MANUAL,
  HANDIN_PROVIDER,
  LTI1P3_PROVIDER,
  TURNITIN_PROVIDER,
  QUIZZES_PROVIDER,
} from '@core/assignments/constants';
import { ResponsiveAssignment } from '@core/assignments/types';
import formatDate from '@core/utils/formatDate';
import {
  getAssignmentProvider,
  getIsCoupledProvider,
  isAssignmentProviderDisabled,
} from '@core/assignments/utils';
import { accessingCurrentSpaceAsStaff } from '@core/selectors/classroom';
import { operations as assignmentOperations } from '../../../../../store/assignments';
import { filterAssignmentParams } from '../utils/utils';
import getHandinUrl from '../utils/getHandinUrl';
import { Lti1p3Launch } from '../../Assignments/Lti1p3Launch/Lti1p3Launch';
import GotItModal from '../CreateAssignment/GotItModal';
import { GRADE_TYPE_LABELS, STATUS_TOOLTIPS } from '../constants';
import { createStyles } from './AssignmentCard.styles';
import AnonymousIcon from './AnonymousIcon';
import Logo from './Logo';
import { getStatus, getStatusLabel, getStatusStyles, STATUS } from './utils';
import AssignmentExtensionsModal from './AssignmentExtensionsModal';
import deleteLti1p3Assignment from '@core/api/lti/deleteLti1p3Assignment';
import { analyticsTrackEvent } from '@core/utils/analytics';
import * as analyticEvents from '@core/constants/analytics';
import showToast from '@core/toasts/operations/showToast.operation';
import { accessToken as getAccessToken } from '@core/selectors/user';
import { getCurrentSpaceId } from '@core/selectors/space';
import { AulaState } from '@core/types/state';
import useHorizontalOverflow from '../hooks/useHorizontalOverflow';
import {
  GOT_IT_TEXT_PARAGRAPH_1,
  GOT_IT_TEXT_PARAGRAPH_2,
} from '@app/constants/texts';
import AssignmentMaterials from '../AssignmentMaterials/AssignmentMaterials';
import LinkedMaterials from './LinkedMaterials/LinkedMaterials';
import { getAssignmentUnavailableReason } from '../availability';
import { AssignmentUnavailableReason } from '../types';

type AssignmentSubmission = {
  displayGrade?: string;
};

type ResponsiveCardAssignment = ResponsiveAssignment & {
  submission?: AssignmentSubmission[];
  parentAssignment?: ResponsiveAssignment;
};

export const getStudentSubmissionState = (
  isEducator: boolean,
  assignment: ResponsiveCardAssignment
) => {
  const submission = !isEducator ? assignment.submission?.[0] : undefined;
  const isManualAssignment = assignment.provider === MANUAL;

  return {
    isSubmitted:
      !isEducator &&
      !isManualAssignment &&
      (Boolean(assignment.isSubmitted) || Boolean(submission)),
    grade: submission?.displayGrade,
  };
};

type Props = {
  assignment: ResponsiveCardAssignment;
  spaceId: string;
  providers: Array<LtiProvider>;
  isEducator?: boolean;
  highlighted?: boolean;
  onMaterialClick?: (
    materialId: string,
    materialTitle?: string,
    assignmentId?: string,
    isMultipleIds?: boolean
  ) => void;
  onTurnitinClick?: (assignment: ResponsiveAssignment) => void;
  onCoupledEditClick?: (assignment: ResponsiveAssignment) => void;
  onMaterialListClick: (assignmentId: string) => void;
  onUnavailableClick: (reason: AssignmentUnavailableReason) => void;
};

type ExportGradesAction = (params: {
  assignment: ResponsiveAssignment & { objectId: string };
  spaceId: string;
}) => ReturnType<typeof exportSubmissionGrades>;

const typedExportSubmissionGrades =
  exportSubmissionGrades as unknown as ExportGradesAction;

const AssignmentCard = ({
  assignment,
  spaceId,
  providers,
  isEducator = false,
  highlighted = false,
  onMaterialClick,
  onTurnitinClick,
  onCoupledEditClick,
  onMaterialListClick,
  onUnavailableClick,
}: Props) => {
  const theme = useTheme();
  const isMdBelow = useMediaQuery(theme.breakpoints.down('md'));
  const styles = createStyles(theme);

  const cardRef = useRef<HTMLDivElement | null>(null);

  const assignmentId = assignment.id;
  const assignmentRoute = `/dashboard/${spaceId}/journey/assignments/${assignmentId}`;

  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [lti1p3Assignment, setLti1p3Assignment] =
    useState<ResponsiveAssignment | null>(null);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [extensionsModalOpen, setExtensionsModalOpen] = useState(false);
  const [editingProviderIconUrl, setEditingProviderIconUrl] = useState<
    string | undefined
  >();
  const [editingProviderDescription, setEditingProviderDescription] = useState<
    string | undefined
  >();

  const dispatch = useDispatch();

  const accessingAsStaff = useSelector(accessingCurrentSpaceAsStaff);
  const baseUrlDomain = useSelector(baseDomain) as string;
  const selectedSpaceId = useSelector((state: AulaState) =>
    getCurrentSpaceId(state)
  );
  const accessToken = useSelector((state: AulaState) =>
    getAccessToken(state, selectedSpaceId ?? '')
  );
  const extensionSubmissionIds = [
    assignment.id || assignment.objectId || assignment.externalId,
    ...(assignment.extensions || []).map(
      (extension: any) => extension?.id || extension?.objectId
    ),
  ].filter(Boolean) as string[];
  const hasExtensions = (assignment.extensions || []).length > 0;
  const hasPrefetchedExtensions = useRef(false);

  const openMenu = (e: MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setMenuAnchorEl(e.currentTarget);
  };

  const closeMenu = () => setMenuAnchorEl(null);

  const isHandin = assignment.provider === HANDIN_PROVIDER;
  const isLti1p3 = assignment.provider === LTI1P3_PROVIDER;
  const isManual = assignment.provider === MANUAL;

  const providerItem = getAssignmentProvider(assignment, providers);

  const isCoupled = providerItem
    ? getIsCoupledProvider(providerItem as any)
    : false;

  const isProviderDisabled = isAssignmentProviderDisabled(
    assignment,
    providers
  );

  const providerName = isLti1p3
    ? providerItem?.description
    : {
        'hand-in': 'Handin',
        handin: 'Handin',
        turnitin: 'Turnitin',
        quizzes: 'Quizzes',
        'lti-1p3': 'Lti-1p3',
      }[assignment.provider];

  const providerIconUrl = providerItem?.iconUrl;

  const linkedMaterialId = assignment.linkedMaterials?.[0]?.id;
  const linkedMaterials = assignment.linkedMaterials || [];

  const isMultiMaterials =
    assignment.linkedMaterials?.length > 1 ? true : false;
  const [openMaterials, setOpenMaterials] = useState<boolean>(false);

  const firstMaterial = linkedMaterials[0];
  const additionalMaterialsCount = linkedMaterials.length - 1;

  const { isSubmitted, grade } = getStudentSubmissionState(
    isEducator,
    assignment
  );

  const isExtension = Boolean(
    assignment.parentAssignmentId && assignment.parentAssignment
  );
  const assignmentTitle = isExtension
    ? assignment.parentAssignment!.title
    : assignment.title;
  const assignmentSubtitle = isExtension ? assignment.title : undefined;

  const handleDeleteAssignment = async () => {
    const effectiveSpaceId = assignment.classRoom?.objectId || spaceId;

    setIsDeleting(true);

    try {
      if (assignment.provider === LTI1P3_PROVIDER) {
        await deleteLti1p3Assignment(effectiveSpaceId, assignment.id);

        analyticsTrackEvent(analyticEvents.DELETE_ASSIGNMENT, {
          assignment: {
            objectId: assignment.id,
          },
          activeClassroom: effectiveSpaceId,
        });
      } else {
        await (dispatch as any)(
          assignmentOperations.editAssignment({
            spaceId: effectiveSpaceId,
            assignment: {
              ...filterAssignmentParams(assignment),
              status: 'trash',
            },
            accessToken: undefined,
          })
        );
      }
    } catch {
      dispatch(
        showToast({
          message: 'There was an error while deleting the assignment.',
        })
      );
    } finally {
      setIsDeleting(false);
      setDeleteModalOpen(false);
    }
  };

  const handleMaterialClick = (
    event: React.MouseEvent | React.KeyboardEvent
  ) => {
    event.stopPropagation();

    if (isMultiMaterials) {
      setOpenMaterials(true);
      onMaterialListClick(assignment.id ?? assignment.objectId);
    } else {
      if (linkedMaterialId) {
        onMaterialClick?.(
          linkedMaterialId,
          firstMaterial?.title,
          assignment.id || assignment.objectId
        );
      }
    }
  };

  const handleMaterialKeyDown = (event: React.KeyboardEvent) => {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return null;
    }

    event.preventDefault();

    if (linkedMaterialId) {
      onMaterialClick?.(
        linkedMaterialId,
        firstMaterial?.title,
        assignment.id || assignment.objectId
      );
    }
  };

  const handleOpenExtensionsModal = (
    event: React.MouseEvent | React.KeyboardEvent
  ) => {
    event.stopPropagation();
    setExtensionsModalOpen(true);
  };

  const handleCloseExtensionsModal = () => {
    setExtensionsModalOpen(false);
  };

  const getCurrentAssignmentStatus = () =>
    assignment.provider === MANUAL
      ? STATUS.manualAssignment
      : getStatus({
          startDate: assignment.startDate,
          endDate: assignment.endDate,
          status: assignment.status,
          shouldCloseOnDueDate: assignment.shouldCloseOnDueDate,
        });

  const onClickAssignment = () => {
    const unavailableReason = getAssignmentUnavailableReason({
      assignment,
      assignmentStatus: getCurrentAssignmentStatus(),
      isEducator,
      isStaff: accessingAsStaff,
      isProviderDisabled,
    });

    if (unavailableReason) {
      onUnavailableClick(unavailableReason);
      return;
    }

    pushRoute(assignmentRoute);
  };

  useEffect(() => {
    if (!highlighted) {
      return;
    }

    cardRef.current?.scrollIntoView({
      block: 'center',
      behavior: 'smooth',
    });

    cardRef.current?.focus({
      preventScroll: true,
    });
  }, [highlighted]);

  useEffect(() => {
    if (
      !hasExtensions ||
      !accessToken ||
      extensionSubmissionIds.length === 0 ||
      hasPrefetchedExtensions.current
    ) {
      return;
    }

    hasPrefetchedExtensions.current = true;

    dispatch(
      assignmentOperations.fetchAllSubmissionsIncludingExtensions({
        accessToken,
        assignmentsIds: extensionSubmissionIds,
      }) as any
    );
  }, [
    accessToken,
    assignment.id,
    assignment.extensions?.length,
    dispatch,
    hasExtensions,
    extensionSubmissionIds.join(','),
  ]);

  const assignmentStatus = getCurrentAssignmentStatus();

  const isClosed = assignmentStatus === STATUS.closed;

  const showOpenMenuItem =
    [LTI1P3_PROVIDER, HANDIN_PROVIDER].includes(assignment.provider) &&
    isEducator &&
    !isProviderDisabled;

  const showEditMenuItem =
    [TURNITIN_PROVIDER, QUIZZES_PROVIDER].includes(assignment.provider) &&
    isEducator &&
    !isProviderDisabled &&
    !isCoupled;

  const showEditAssignmentMenuItem =
    isEducator && !isProviderDisabled && isCoupled;

  const isEditEnabledForQuizzes = providerName !== 'Quizzes';

  const toggleHideAssignment = (
    currentSpaceId: string,
    currentAssignment: ResponsiveAssignment
  ) => {
    if (!currentSpaceId || !currentAssignment) {
      return;
    }

    return dispatch(
      assignmentOperations.toggleHideAssignment({
        spaceId: currentSpaceId,
        assignment: currentAssignment,
      })
    );
  };

  const handleOpenInTool = ({
    assignment: currentAssignment,
    editingCoupled,
    iconUrl,
    description,
  }: {
    assignment: ResponsiveAssignment;
    editingCoupled: boolean;
    iconUrl?: string;
    description?: string;
  }) => {
    if (!editingCoupled && currentAssignment?.provider === LTI1P3_PROVIDER) {
      setLti1p3Assignment(currentAssignment);
      setEditingProviderIconUrl(iconUrl);
      setEditingProviderDescription(description);
      return;
    }

    if (currentAssignment.provider === HANDIN_PROVIDER) {
      const assignmentSpace =
        currentAssignment?.space ||
        (currentAssignment as { classId?: string })?.classId;

      const handinSpaceId =
        assignmentSpace || currentAssignment?.classRoom?.objectId || spaceId;

      const externalUrl = getHandinUrl({
        baseDomain: baseUrlDomain,
        space: handinSpaceId,
        assignment: currentAssignment?.externalId,
        search: 'editAssignment=true&source=ma',
      });

      window.open(externalUrl, '_blank');
    }
  };

  const ltiLaunchSuccess = () => {
    setLti1p3Assignment(null);
    setShowSuccessDialog(isEducator);
  };

  const ltiLaunchError = () => {
    setLti1p3Assignment(null);
    setShowSuccessDialog(false);

    dispatch(
      showToast({
        message: 'There was an error when trying to open the LTI tool.',
      })
    );
  };

  /*
   * Visibility menu item.
   */
  const getVisibilityMenuItem = () => {
    if (!isEducator) {
      return {};
    }

    const action = assignment.isHidden
      ? {
          id: 'show-assignment',
          label: 'Show assignment',
          icon: <Icons.VisibilityOutlined fontSize="small" />,
          danger: false,
        }
      : {
          id: 'hide-assignment',
          label: 'Hide assignment',
          icon: <Icons.VisibilityOffOutlined fontSize="small" />,
          danger: false,
        };

    return {
      ...action,
      onClick: () => toggleHideAssignment(spaceId, assignment),
    };
  };

  const menuItems = [
    getVisibilityMenuItem(),

    showOpenMenuItem && {
      id: 'open-in-provider',
      label: `Open in ${providerName}`,
      icon: <Icons.OpenInNew fontSize="small" />,
      danger: false,
      onClick: () =>
        handleOpenInTool({
          assignment,
          editingCoupled: false,
          iconUrl: providerIconUrl,
          description: providerName,
        }),
    },

    showEditMenuItem &&
      isEditEnabledForQuizzes && {
        id: 'edit-in-provider',
        label: 'Edit assignment',
        icon: <Icons.EditOutlined fontSize="small" />,
        danger: false,
      },

    showEditAssignmentMenuItem && {
      id: 'edit-assignment',
      label: 'Edit assignment',
      icon: <Icons.EditOutlined fontSize="small" />,
      danger: false,
      onClick: () => onCoupledEditClick?.(assignment),
    },

    (isEducator || accessingAsStaff) && {
      id: 'export-grades',
      label: 'Export grades',
      icon: <Icons.ImportExportOutlined fontSize="small" />,
      onClick: () =>
        dispatch(
          typedExportSubmissionGrades({
            assignment: {
              ...assignment,
              objectId: assignment.objectId || assignment.id,
            },
            spaceId: assignment.classRoom?.objectId || spaceId,
          })
        ),
      danger: false,
    },

    isEducator &&
      isHandin &&
      (isClosed
        ? {
            id: 're-open',
            label: 'Re-open',
            icon: <Icons.LockOpenOutlined fontSize="small" />,
            danger: false,
            onClick: () =>
              dispatch(
                assignmentOperations.reopenAssignment({
                  spaceId: assignment.classRoom?.objectId || spaceId,
                  assignment,
                })
              ),
          }
        : {
            id: 'close',
            label: 'Close',
            icon: <Icons.LockOutlined fontSize="small" />,
            danger: false,
            onClick: () =>
              dispatch(
                assignmentOperations.closeAssignment({
                  spaceId: assignment.classRoom?.objectId || spaceId,
                  assignment,
                })
              ),
          }),

    isEducator &&
      isHandin &&
      !assignment.extensions?.length &&
      (assignment.isAnonymised
        ? {
            id: 'reveal-students',
            label: 'Reveal students',
            icon: <Icons.PeopleOutlined fontSize="small" />,
            danger: false,
            onClick: () =>
              dispatch(
                assignmentOperations.editAssignment({
                  spaceId,
                  assignment: {
                    ...filterAssignmentParams(assignment),
                    isAnonymised: false,
                  },
                  accessToken: undefined,
                })
              ),
          }
        : {
            id: 'anonymise-students',
            label: 'Anonymise students',
            icon: <Icons.PeopleOutlined fontSize="small" />,
            danger: false,
            onClick: () =>
              dispatch(
                assignmentOperations.editAssignment({
                  spaceId,
                  assignment: {
                    ...filterAssignmentParams(assignment),
                    isAnonymised: true,
                  },
                  accessToken: undefined,
                })
              ),
          }),

    isEducator &&
      (!isLti1p3 || isCoupled) && {
        id: 'delete',
        label: 'Delete',
        icon: <Icons.DeleteOutlined fontSize="small" />,
        danger: true,
        onClick: () => setDeleteModalOpen(true),
      },
  ].filter(Boolean);

  let statusStyle = styles.status;

  if (assignmentStatus === STATUS.pastDueDate) {
    statusStyle = styles.pastDueDate;
  } else if (assignmentStatus === STATUS.manualAssignment) {
    statusStyle = styles.manualStatus;
  }

  const statusTooltip =
    assignmentStatus && assignmentStatus !== STATUS.manualAssignment
      ? STATUS_TOOLTIPS[assignmentStatus]
      : undefined;

  const gradingLabel = assignment.gradeType
    ? GRADE_TYPE_LABELS[assignment.gradeType]
    : undefined;

  const dateFooter =
    assignment.startDate && assignment.endDate
      ? assignmentStatus === STATUS.upcoming
        ? `Open: ${formatDate(assignment.startDate)} - Due: ${formatDate(
            assignment.endDate
          )}`
        : `Due: ${formatDate(assignment.endDate)}`
      : undefined;

  const footerText = grade
    ? `Grade - ${grade}`
    : isManual && gradingLabel
    ? `${gradingLabel} grading`
    : dateFooter;

  const [nameRef, isOverflowing] = useHorizontalOverflow<HTMLDivElement>();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const titleTooltipDisabled = !isDesktop || !isOverflowing;
  const isDark = theme.palette.mode == 'dark';

  const renderStatusLabel = (status: string) => (
    <Box
      sx={{
        ...statusStyle,
        ...getStatusStyles(status, isDark),
      }}
      tabIndex={0}
    >
      {getStatusLabel(status)}
    </Box>
  );

  return (
    <>
      <Box
        ref={cardRef}
        onClick={onClickAssignment}
        sx={{
          ...styles.cardWrapper,
          ...(!highlighted && isDesktop ? styles.cardWrapperHover : {}),
          ...(highlighted ? styles.highlightedCard : {}),
        }}
        data-highlighted={highlighted ? 'true' : undefined}
      >
        <Box sx={styles.cardHeader}>
          <Box sx={styles.cardDetails}>
            <Logo assignment={assignment} providers={providers} />

            {assignmentStatus &&
              (statusTooltip ? (
                <Tooltip title={statusTooltip}>
                  {renderStatusLabel(assignmentStatus)}
                </Tooltip>
              ) : (
                renderStatusLabel(assignmentStatus)
              ))}

            {isSubmitted && (
              <Tooltip title={STATUS_TOOLTIPS.submitted}>
                <Box sx={styles.submittedStatus} tabIndex={0}>
                  Submitted
                </Box>
              </Tooltip>
            )}

            {!!assignment.groupSet && (
              <Tooltip
                title="This is a group assignment"
                disableFocusListener={isMdBelow}
                disableHoverListener={isMdBelow}
                disableTouchListener={isMdBelow}
              >
                <Box>
                  <IconButton
                    sx={styles.headerIcon}
                    role="img"
                    tabIndex={0}
                    aria-label="Group assignment"
                  >
                    <Icons.GroupOutlined />
                  </IconButton>
                </Box>
              </Tooltip>
            )}

            {assignment.isAnonymised && (
              <Tooltip
                title="This assignment is anonymised"
                disableFocusListener={isMdBelow}
                disableHoverListener={isMdBelow}
                disableTouchListener={isMdBelow}
              >
                <Box>
                  <IconButton
                    sx={styles.headerIcon}
                    role="img"
                    tabIndex={0}
                    aria-label="Anonymised assignment"
                  >
                    <AnonymousIcon />
                  </IconButton>
                </Box>
              </Tooltip>
            )}

            {assignment.isHidden && (
              <Tooltip
                title="This assignment is hidden from students"
                disableFocusListener={isMdBelow}
                disableHoverListener={isMdBelow}
                disableTouchListener={isMdBelow}
              >
                <Box>
                  <IconButton
                    sx={styles.headerIcon}
                    role="img"
                    tabIndex={0}
                    aria-label="Assignment hidden from students"
                  >
                    <Icons.VisibilityOffOutlined />
                  </IconButton>
                </Box>
              </Tooltip>
            )}
          </Box>

          {!isManual && (isEducator || accessingAsStaff) && (
            <Box sx={styles.menuIcon}>
              <Tooltip
                title="Assignment options"
                disableFocusListener={isMdBelow}
                disableHoverListener={isMdBelow}
                disableTouchListener={isMdBelow}
              >
                <Box
                  component="span"
                  sx={{
                    display: 'inline-flex',
                  }}
                >
                  <IconButton
                    id="assignment-options"
                    type="button"
                    aria-label="Assignment options"
                    onClick={openMenu}
                  >
                    <Icons.MoreVert fontSize="small" />
                  </IconButton>
                </Box>
              </Tooltip>

              <Menu
                anchorEl={menuAnchorEl}
                open={Boolean(menuAnchorEl)}
                onClose={closeMenu}
                aria-labelledby="assignment-options"
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                PaperProps={{
                  style: {
                    backgroundColor: isDark ? '#363636' : undefined,
                    color: isDark ? '#ffffff' : undefined,
                    width: '240px',
                    marginTop: '4px',
                  },
                }}
                sx={{
                  '& .MuiPaper-root': {
                    backgroundColor: isDark ? '#363636 !important' : undefined,
                    color: isDark ? '#ffffff' : undefined,
                    width: '240px',
                    marginTop: '4px',
                  },
                  '& .MuiMenu-paper': {
                    backgroundColor: isDark ? '#363636 !important' : undefined,
                  },
                  '& .MuiPopover-paper': {
                    backgroundColor: isDark ? '#363636 !important' : undefined,
                  },
                  '& .MuiMenu-list': {
                    backgroundColor: isDark ? '#363636 !important' : undefined,
                  },
                  '& .MuiList-root': {
                    backgroundColor: isDark ? '#363636 !important' : undefined,
                  },
                  '& .MuiListItemText-primary': {
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  },
                }}
              >
                {menuItems.map((item: any) => (
                  <MenuItem
                    key={item.id}
                    icon={item.icon}
                    label={item.label}
                    data-testid={`assignment-${item.id}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      closeMenu();
                      if (
                        item.id === 'edit-in-provider' &&
                        assignment.provider === TURNITIN_PROVIDER
                      ) {
                        onTurnitinClick?.(assignment);
                      } else {
                        item.onClick?.();
                      }
                    }}
                  />
                ))}
              </Menu>
            </Box>
          )}
        </Box>

        {assignmentSubtitle && (
          <Box sx={styles.subtitle}>{assignmentSubtitle}</Box>
        )}

        <Tooltip
          placement="top"
          title={assignmentTitle}
          disableFocusListener={titleTooltipDisabled}
          disableHoverListener={titleTooltipDisabled}
          disableTouchListener={titleTooltipDisabled}
        >
          <Box sx={styles.title} component={'div'} ref={nameRef}>
            {assignmentTitle}
          </Box>
        </Tooltip>

        {assignment.extensions && (
          <Box
            sx={styles.extensions}
            onClick={handleOpenExtensionsModal}
            role="button"
            tabIndex={0}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                handleOpenExtensionsModal(event);
              }
            }}
            aria-label={`View ${assignment.extensions.length} extensions`}
          >
            <Icons.ExtensionOutlined sx={styles.extensionsIcon} />
            <span>{assignment.extensions.length} extensions available</span>
          </Box>
        )}

        {firstMaterial && (
          <LinkedMaterials
            title={firstMaterial?.title || null}
            onClick={handleMaterialClick}
            onKeyDown={handleMaterialKeyDown}
            remainingMaterialCount={additionalMaterialsCount}
          />
        )}

        {footerText && (
          <Box sx={{ ...styles.endDate, ...(grade ? styles.grade : {}) }}>
            {footerText}
          </Box>
        )}
      </Box>

      {lti1p3Assignment && (
        <Lti1p3Launch
          providerId={lti1p3Assignment.providerId!}
          resourceLinkId={lti1p3Assignment.resourceLinkId!}
          resourceLinkTitle={lti1p3Assignment.title}
          assignmentId={lti1p3Assignment.id || lti1p3Assignment.objectId}
          onSuccessLaunch={ltiLaunchSuccess}
          onErrorLaunch={ltiLaunchError}
        />
      )}

      {showSuccessDialog && (
        <GotItModal
          isVisible={showSuccessDialog}
          logo={editingProviderIconUrl}
          name={editingProviderDescription || ''}
          textParagraph1={GOT_IT_TEXT_PARAGRAPH_1}
          textParagraph2={GOT_IT_TEXT_PARAGRAPH_2}
          onClose={() => setShowSuccessDialog(false)}
        />
      )}

      <DeleteAssignmentModal
        open={deleteModalOpen}
        assignmentTitle={assignment.title}
        loading={isDeleting}
        onClose={() => {
          if (!isDeleting) {
            setDeleteModalOpen(false);
          }
        }}
        onConfirm={handleDeleteAssignment}
      />
      <AssignmentExtensionsModal
        open={extensionsModalOpen}
        assignment={assignment}
        onClose={handleCloseExtensionsModal}
      />
      {openMaterials && (
        <AssignmentMaterials
          open={openMaterials}
          onClose={() => setOpenMaterials(false)}
          linkedMaterials={linkedMaterials}
        />
      )}
    </>
  );
};

export default AssignmentCard;
